import { useSelector } from 'react-redux';
import { hasSections } from 'volto-uni-bonn/components/Section/topLevel';
import React, { useState, useCallback } from 'react';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import without from 'lodash/without';
import {
  emptyBlocksForm,
  getBlocksLayoutFieldname,
} from '@plone/volto/helpers/Blocks/Blocks';
import { withBlockExtensions } from '@plone/volto/helpers//Extensions';
import BodyComponent from '@eeacms/volto-group-block/components/manage/Blocks/Group/Body';

import config from '@plone/volto/registry';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import BlockDataForm from '@plone/volto/components/manage/Form/BlockDataForm';
import BlocksToolbar from '@plone/volto/components/manage/Form/BlocksToolbar';
import PropTypes from 'prop-types';
import { Segment } from 'semantic-ui-react';
import EditSchema from '@eeacms/volto-group-block/components/manage/Blocks/Group/EditSchema';

import CounterComponent, {
  countTextInBlocks,
} from '@eeacms/volto-group-block/components/manage/Blocks/Group/CounterComponent';
import '@eeacms/volto-group-block/components/manage/Blocks/Group/editor.less';
import { defineMessages, injectIntl } from 'react-intl';
import { compose } from 'redux';

const messages = defineMessages({
  sectionGroupSettings: {
    id: 'Section',
    defaultMessage: 'Section',
  },
  section: {
    id: 'sectionTitle',
    defaultMessage: 'Section',
  },
});

const Edit = (props) => {
  const linoActive = useSelector(
    (state) =>
      state.assistant?.tabIndex != null &&
      state.sidebar?.tab === state.assistant.tabIndex,
  );
  const { block, data, onChangeBlock, selected, formDescription } = props;
  const [multiSelected, setMultiSelected] = useState([]);
  const data_blocks = data?.data?.blocks;
  const childBlocksForm = isEmpty(data_blocks) ? emptyBlocksForm() : data.data;

  const [selectedBlock, setSelectedBlock] = useState(
    childBlocksForm.blocks_layout.items[0],
  );

  const handleKeyDown = (
    e,
    index,
    block,
    node,
    {
      disableEnter = false,
      disableArrowUp = false,
      disableArrowDown = false,
    } = {},
  ) => {
    const hasblockActive = !!selectedBlock;
    if (e.key === 'ArrowUp' && !disableArrowUp && !hasblockActive) {
      props.onFocusPreviousBlock(block, node);
      e.preventDefault();
    }
    if (e.key === 'ArrowDown' && !disableArrowDown && !hasblockActive) {
      props.onFocusNextBlock(block, node);
      e.preventDefault();
    }
    if (e.key === 'Enter' && !disableEnter && !hasblockActive) {
      props.onAddBlock(config.settings.defaultBlockType, index + 1);
      e.preventDefault();
    }
  };

  const onSelectBlock = useCallback(
    (id, isMultipleSelection, event, activeBlock) => {
      let newMultiSelected = [];
      let selected = id;

      if (isMultipleSelection) {
        selected = null;
        const blocksLayoutFieldname = getBlocksLayoutFieldname(data?.data);
        const blocks_layout = data?.data[blocksLayoutFieldname].items;
        if (event.shiftKey) {
          const anchor =
            multiSelected.length > 0
              ? blocks_layout.indexOf(multiSelected[0])
              : blocks_layout.indexOf(activeBlock);
          const focus = blocks_layout.indexOf(id);
          if (anchor === focus) {
            newMultiSelected = [id];
          } else if (focus > anchor) {
            newMultiSelected = [...blocks_layout.slice(anchor, focus + 1)];
          } else {
            newMultiSelected = [...blocks_layout.slice(focus, anchor + 1)];
          }
        }
        if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
          if (multiSelected.includes(id)) {
            selected = null;
            newMultiSelected = without(multiSelected, id);
          } else {
            newMultiSelected = [...(multiSelected || []), id];
          }
        }
      }

      setSelectedBlock(selected);
      setMultiSelected(newMultiSelected);
    },
    [data.data, multiSelected],
  );

  const changeBlockData = (newBlockData) => {
    if (hasSections(newBlockData)) return;
    let pastedBlocks = newBlockData.blocks_layout.items.filter((blockID) => {
      return !data?.data?.blocks_layout.items.find((x) => x === blockID);
    });
    const selectedIndex =
      data.data.blocks_layout.items.indexOf(selectedBlock) + 1;
    const newChildData = {
      ...data?.data,
      ...newBlockData,
      blocks_layout: {
        items: [
          ...data.data.blocks_layout.items.slice(0, selectedIndex),
          ...pastedBlocks,
          ...data.data.blocks_layout.items.slice(selectedIndex),
        ],
      },
    };
    const newData = {
      ...data,
      data: newChildData,
    };
    if (data.maxChars) {
      newData.charCount = countTextInBlocks(
        newChildData,
        data.ignoreSpaces,
        data.maxChars,
      );
    }
    onChangeBlock(block, newData);
  };

  React.useEffect(() => {
    if (
      isEmpty(data_blocks) &&
      childBlocksForm.blocks_layout.items[0] !== selectedBlock
    ) {
      setSelectedBlock(childBlocksForm.blocks_layout.items[0]);
      const newData = {
        ...data,
        data: childBlocksForm,
      };
      if (data.maxChars) {
        newData.charCount = 0;
      }
      onChangeBlock(block, newData);
    }
  }, [onChangeBlock, childBlocksForm, selectedBlock, block, data, data_blocks]);

  // Get editing instructions from block settings or props
  let instructions = data?.instructions?.data || data?.instructions;
  if (!instructions || instructions === '<p><br/></p>') {
    instructions = formDescription;
  }

  return (
    <fieldset
      role="presentation"
      id={props.data.id}
      className={cx('section-block', props.data.className, {
        'disable-inner-buttons': data.disableInnerButtons,
      })}
      onKeyDown={(e) => {
        handleKeyDown(e, props.index, props.block, props.blockNode.current);
      }}
      // The tabIndex is required for the keyboard navigation
      /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
      tabIndex={-1}
    >
      <legend
        onClick={() => {
          setSelectedBlock();
          if (!linoActive) props.setSidebarTab(1);
        }}
        aria-hidden="true"
      >
        {data.title || props.intl.formatMessage(messages.section)}
      </legend>
      <BodyComponent
        {...props}
        isEditMode={true}
        selectedBlock={selectedBlock}
        setSelectedBlock={setSelectedBlock}
        multiSelected={multiSelected}
        setMultiSelected={setMultiSelected}
        onSelectBlock={onSelectBlock}
        childBlocksForm={childBlocksForm}
      />
      {selected ? (
        <BlocksToolbar
          selectedBlock={selectedBlock}
          selectedBlocks={multiSelected}
          onSetSelectedBlocks={(blockIds) => {
            setMultiSelected(blockIds);
          }}
          formData={data.data}
          onSelectBlock={(id, l, e) => {
            const isMultipleSelection = e
              ? e.shiftKey || e.ctrlKey || e.metaKey
              : false;

            onSelectBlock(id, isMultipleSelection, e, selectedBlock);
          }}
          onChangeBlocks={(newBlockData) => {
            changeBlockData(newBlockData);
          }}
        />
      ) : (
        ''
      )}
      {props.data.maxChars && (
        <CounterComponent {...props} setSelectedBlock={setSelectedBlock} />
      )}
      <SidebarPortal selected={selected && !selectedBlock}>
        {instructions && (
          <Segment attached>
            <div dangerouslySetInnerHTML={{ __html: instructions }} />
          </Segment>
        )}
        {!data?.readOnlySettings && (
          <BlockDataForm
            schema={EditSchema(props.intl)}
            title={props.intl.formatMessage(messages.sectionGroupSettings)}
            formData={data}
            onChangeField={(id, value) => {
              const newData = {
                ...props.data,
                [id]: value,
              };
              const effectiveMaxChars =
                id === 'maxChars' ? value : props.data.maxChars;
              const effectiveIgnoreSpaces =
                id === 'ignoreSpaces' ? value : props.data.ignoreSpaces;
              if (effectiveMaxChars) {
                newData.charCount = countTextInBlocks(
                  props.data?.data,
                  effectiveIgnoreSpaces,
                  effectiveMaxChars,
                );
              } else {
                delete newData.charCount;
              }
              props.onChangeBlock(props.block, newData);
            }}
          />
        )}
      </SidebarPortal>
    </fieldset>
  );
};

Edit.propTypes = {
  block: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  manage: PropTypes.bool.isRequired,
};

export default compose(injectIntl, withBlockExtensions)(Edit);
